import { AlertCircle, Check, Loader2, X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";

import { api } from "../api/client";
import type { Proposal, UnidentifiedFlagPayload } from "../api/types";

interface Props {
  /** All proposals from the latest agent turn — this CTA filters to
   *  `unidentified_product_flag` and renders one banner row per flag. */
  proposals: Proposal[];
  /** Called once a flag is validated or refused, so the parent can
   *  drop it from `lastProposals` and the banner stops re-rendering. */
  onResolved: (proposalId: string) => void;
}

/**
 * Pending `unidentified_product_flag` proposals surface unknown product
 * names the agent heard but couldn't match to a canonical item. The
 * operator decides on the spot:
 *   - **Valider** → the row lands in the admin queue
 *     (`AdminUnidentifiedProducts`), where an administrateur later
 *     resolves it (alias, create new canonical, or discard).
 *   - **Refuser** → the flag is discarded; nothing reaches the queue.
 *
 * Banner copy mirrors `OrphanProposalsCTA` / `PendingPanneauCard` so the
 * "agent suggested this — Valider/Refuser" affordance reads consistently
 * across the operator panneau workspace.
 *
 * UI contract: per PRD §C, the user MUST click Valider for the flag to
 * enter the admin queue — the agent never auto-validates.
 */
export function UnidentifiedFlagsCTA({ proposals, onResolved }: Props) {
  // Per-proposal validate / reject. Panneau-id-less endpoints because
  // a flag isn't tied to any panneau — runtime.commit_proposal
  // dispatches on the proposal type to insert the admin row.
  const validate = useMutation({
    mutationFn: (proposalId: string) =>
      api<{ committed: string[]; skipped: string[] }>(
        `/api/proposals/${proposalId}/validate`,
        { method: "POST" },
      ),
    onSuccess: (_data, proposalId) => {
      onResolved(proposalId);
    },
  });
  const reject = useMutation({
    mutationFn: (proposalId: string) =>
      api<void>(`/api/proposals/${proposalId}/reject`, { method: "POST" }),
    onSuccess: (_data, proposalId) => {
      onResolved(proposalId);
    },
  });

  const pending = proposals.filter(
    (p) => p.type === "unidentified_product_flag",
  );
  if (pending.length === 0) return null;

  return (
    <div className="max-w-6xl mx-auto mt-2 mb-1 px-4">
      <div className="rounded-xl bg-chip-orange/15 ring-1 ring-chip-orange/40 overflow-hidden">
        <header className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-ink dark:text-ink-dark border-b border-chip-orange/20">
          <AlertCircle className="h-3.5 w-3.5 text-chip-orange shrink-0" />
          <span>
            {pending.length === 1
              ? "1 produit non identifié à valider"
              : `${pending.length} produits non identifiés à valider`}
          </span>
        </header>
        <ul className="divide-y divide-chip-orange/20">
          {pending.map((p) => {
            const payload = p.payload as Partial<UnidentifiedFlagPayload>;
            const rawName = String(payload.raw_name ?? "");
            const sourceQuote = String(
              payload.source_quote ?? p.source_quote ?? "",
            );
            const isValidating =
              validate.isPending && validate.variables === p.id;
            const isRejecting =
              reject.isPending && reject.variables === p.id;
            return (
              <li
                key={p.id}
                className="px-3 py-2 flex flex-wrap items-center gap-2 text-sm"
              >
                <div className="flex-1 min-w-0 text-ink dark:text-ink-dark">
                  <span className="font-medium">
                    Produit non identifié à valider :
                  </span>{" "}
                  <span className="italic">« {rawName} »</span>
                  {sourceQuote && (
                    <>
                      {" "}
                      <span className="text-muted dark:text-muted-dark">
                        dans
                      </span>{" "}
                      <span className="italic text-muted dark:text-muted-dark">
                        « {sourceQuote} »
                      </span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => validate.mutate(p.id)}
                  disabled={isValidating || isRejecting}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink/90 disabled:opacity-50"
                  aria-label={`Valider le signalement de ${rawName}`}
                >
                  {isValidating ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )}
                  Valider
                </button>
                <button
                  type="button"
                  onClick={() => reject.mutate(p.id)}
                  disabled={isValidating || isRejecting}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white dark:bg-surface-dark ring-1 ring-black/15 dark:ring-white/15 text-ink dark:text-ink-dark text-xs hover:ring-ink/40 dark:hover:ring-white/40 disabled:opacity-50"
                  aria-label={`Refuser le signalement de ${rawName}`}
                >
                  {isRejecting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                  Refuser
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
